import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountries } from '@/api/queries/reference';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import styles from './CountryPicker.module.css';

interface CountryPickerProps {
  value?: string | null;
  onPick: (code: string, name: string) => void;
  exclude?: string[];
}

export function CountryPicker({ value, onPick, exclude = [] }: CountryPickerProps): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading } = useCountries();
  const [q, setQ] = useState('');

  const list = useMemo(() => {
    const all = data ?? [];
    const filtered = all.filter((c) => !exclude.includes(c.code));
    if (!q) return filtered;
    const lower = q.toLowerCase();
    return filtered.filter(
      (c) => c.name.toLowerCase().includes(lower) || c.code.toLowerCase().includes(lower),
    );
  }, [data, q, exclude]);

  return (
    <div className={styles.wrap}>
      <Input
        autoFocus
        placeholder={t('common.search_placeholder')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className={styles.scroll}>
        {isLoading && <Spinner />}
        {list.map((c) => (
          <button
            key={c.code}
            type="button"
            className={`${styles.row} ${value === c.code ? styles.active : ''}`}
            onClick={() => onPick(c.code, c.name)}
          >
            <span className={styles.flag}>{c.flag_emoji ?? '🏳️'}</span>
            <span className={styles.name}>{c.name}</span>
            <span className={styles.code}>{c.code}</span>
          </button>
        ))}
        {!isLoading && list.length === 0 && <div className={styles.empty}>—</div>}
      </div>
    </div>
  );
}
